"use client";

import { useEffect } from "react";
import { create } from "zustand";
import { normalizeField } from "@/features/filters/filterDefs";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import type { OrderOption } from "@/types";

/**
 * An order option as the items API's `orderBy`.
 *
 * The legacy order string was `+properties.field::lang`, carrying the direction,
 * the document prefix and the language all at once. Only the direction is still
 * meaningful: the property path is normalised the same way a filter's is, and
 * the language is already a request parameter — which is what `isLocalized`
 * used to select and no longer needs to.
 */
export function orderByParam(order: OrderOption | null): string | undefined {
  const field = normalizeField(order?.field);
  if (!field) return undefined;
  return order?.direction === "desc" ? `-${field}` : field;
}

interface ItemsOrderState {
  order: OrderOption | null;
  setOrder: (order: OrderOption | null) => void;
}

export const useItemsOrderStore = create<ItemsOrderState>()((set) => ({
  order: null,
  setOrder: (order) => set({ order }),
}));

/**
 * The result list's sort order, seeded from the project's `listOrderOptions`.
 *
 * The store starts empty and the project's default is applied in an effect rather
 * than at creation, because a zustand store is module-level while the config is
 * per-tenant and per-request — initialising from config at module scope would
 * bake the first tenant's default in for every later one.
 */
export function useItemsOrder() {
  const { listOrderOptions, showListOrder } = useProjectConfigs();
  const order = useItemsOrderStore((state) => state.order);
  const setOrder = useItemsOrderStore((state) => state.setOrder);

  useEffect(() => {
    if (order || !listOrderOptions?.length) return;
    // Fall back to the first option: a project that defines an order but marks
    // none default still has a deterministic one.
    setOrder(
      listOrderOptions.find((option) => option.isDefault) ??
        listOrderOptions[0]
    );
  }, [listOrderOptions, order, setOrder]);

  return { order, setOrder, listOrderOptions, showListOrder };
}
